import {
  DEPENDENT_CONTROL_TYPES,
  findReportCheckpointByReference,
  findTemplateCheckpointByReference,
  getDependency,
  getMappedOptionsForTriggerValue,
  getTriggerAnswerValue,
  isAnswerValidForMapping,
  listDependentsOfTrigger,
  parseCheckpointReference,
} from '@/utils/checkpointDependency'

function getPreTriggerHelperText(triggerTitle: string): string {
  return `Select ${triggerTitle} to filter options`
}

function getTriggerTitle(
  templateSections: any[],
  reference: string | null | undefined
): string {
  const triggerTemplateCp = findTemplateCheckpointByReference(
    templateSections,
    reference
  )
  if (triggerTemplateCp?.title) return triggerTemplateCp.title
  if (triggerTemplateCp?.name) return triggerTemplateCp.name
  return parseCheckpointReference(reference)?.checkpointName || 'dependency'
}

/**
 * Resolve select options for report editing.
 *
 * Isolation: only `select` / `select_dropdown` with a real
 * `options.dependency.reference` use the template listing.
 * All other checkpoints keep the report `dataSource` unchanged.
 */
export function resolveDependentSelectState({
  checkpointName,
  controlType,
  dataSource = [],
  templateSections = [],
  reportSections = [],
  selectDropdownType: _selectDropdownType,
}: {
  checkpointName: string
  controlType: string
  dataSource?: any[]
  templateSections?: any[]
  reportSections?: any[]
  selectDropdownType?: string
}) {
  const passthrough = {
    options: dataSource || [],
    disabled: false,
    helperText: '',
    dependency: null as ReturnType<typeof getDependency>,
    triggerValue: null as string | null,
  }

  if (
    !(DEPENDENT_CONTROL_TYPES as readonly string[]).includes(controlType) ||
    !templateSections?.length
  ) {
    return passthrough
  }

  // Find template checkpoint by name within sections, then read dependency.reference
  let templateCheckpoint: any = null
  for (const section of templateSections) {
    for (const sub of section.sub_sections || []) {
      const cp = (sub.checkpoints || []).find((c) => c.name === checkpointName)
      if (cp) {
        templateCheckpoint = cp
        break
      }
    }
    if (templateCheckpoint) break
  }

  const dependency = getDependency(templateCheckpoint)
  // No Depends on configured → keep report data_source (existing behavior)
  if (!dependency?.reference) {
    return passthrough
  }

  const triggerTitle = getTriggerTitle(templateSections, dependency.reference)
  const triggerReportCp = findReportCheckpointByReference(
    reportSections,
    dependency.reference
  )
  const triggerValue = getTriggerAnswerValue(triggerReportCp)

  // Depends-on only: listing always from template checkpoint data_source
  const templateDataSource = templateCheckpoint?.data_source || []

  if (!triggerValue) {
    return {
      options: [],
      disabled: true,
      helperText: getPreTriggerHelperText(triggerTitle),
      dependency,
      triggerValue: null,
    }
  }

  const options = getMappedOptionsForTriggerValue(
    dependency,
    triggerValue,
    templateDataSource
  )
  return {
    options,
    disabled: false,
    helperText: options.length
      ? ''
      : 'No options are available for the selected trigger value.',
    dependency,
    triggerValue,
  }
}

export function sanitizeSelectValueForDependency({
  value,
  checkpointName,
  controlType,
  templateSections = [],
  reportSections = [],
  selectDropdownType: _selectDropdownType,
}: {
  value: any
  checkpointName: string
  controlType: string
  templateSections?: any[]
  reportSections?: any[]
  selectDropdownType?: string
}) {
  if (
    !(DEPENDENT_CONTROL_TYPES as readonly string[]).includes(controlType) ||
    !value ||
    !templateSections?.length
  ) {
    return value
  }

  let templateCheckpoint: any = null
  for (const section of templateSections) {
    for (const sub of section.sub_sections || []) {
      const cp = (sub.checkpoints || []).find((c) => c.name === checkpointName)
      if (cp) {
        templateCheckpoint = cp
        break
      }
    }
    if (templateCheckpoint) break
  }

  const dependency = getDependency(templateCheckpoint)
  if (!dependency?.reference) return value

  const triggerReportCp = findReportCheckpointByReference(
    reportSections,
    dependency.reference
  )
  const triggerValue = getTriggerAnswerValue(triggerReportCp)

  if (!isAnswerValidForMapping(value, dependency, triggerValue)) {
    return null
  }
  return value
}

export function getInvalidDependentsToClear({
  reference,
  newTriggerValue,
  templateSections = [],
  reportSections = [],
}: {
  reference: string
  newTriggerValue: string | null
  templateSections?: any[]
  reportSections?: any[]
}) {
  if (!reference || !templateSections?.length) return []

  const dependents = listDependentsOfTrigger(templateSections, reference)
  if (!dependents.length) return []

  return dependents
    .map((dependentTemplate) => {
      // Resolve dependent in report by walking template path from its own dependency is not needed —
      // find by name within report (unique within template validation)
      let dependentReport: any = null
      for (const section of reportSections || []) {
        for (const sub of section.subSections || section.sub_sections || []) {
          const cp = (sub.checkpoints || []).find(
            (c) => c.name === dependentTemplate.name
          )
          if (cp) {
            dependentReport = cp
            break
          }
        }
        if (dependentReport) break
      }
      if (!dependentReport?.id) return null
      if (
        isAnswerValidForMapping(
          dependentReport.value,
          getDependency(dependentTemplate),
          newTriggerValue
        )
      ) {
        return null
      }
      return {
        id: dependentReport.id,
        name: dependentTemplate.name,
        control_type: dependentReport.control_type,
      }
    })
    .filter(Boolean)
}

// Re-export for callers that resolve trigger via reference
export { findTemplateCheckpointByReference, findReportCheckpointByReference }
